// Copyright 2017-2024 @polkadot/apps authors & contributors
// SPDX-License-Identifier: Apache-2.0

import type { NormalizedCacheObject } from '@apollo/client';

import { ApolloClient, ApolloLink, execute, HttpLink, InMemoryCache, Observable, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import { getDefaultNetwork, loadNetworkConfig } from '@polkadot/apps-config';
import { dunipodClientState } from '@polkadot/react-api/state';
import { settings } from '@polkadot/ui-settings';

import DUNITER_CONFIG_RAW from '../../../config/duniter.json' with { type: 'json' };

// Declare the Window extension for Dunipod endpoints. Nothing in the app reads
// it: it is kept purely so the resolved endpoint list can be inspected from the
// browser console when diagnosing which pod a session ended up on.
declare global {
  interface Window {
    __DUNIPOD_ENDPOINTS__?: string[];
  }
}

// --- Network Configuration ---

// Client state
let currentClient: ApolloClient<NormalizedCacheObject> | null = null;
let currentEndpointIndex = 0;
let ENDPOINTS: string[] = [];
let currentNetwork: 'g1' | 'gtest' | 'gdev' = getDefaultNetwork();

// Build a map of provider URLs to network names from the config
const providerToNetwork = new Map<string, 'g1' | 'gtest' | 'gdev'>();

for (const network of ['gdev', 'gtest', 'g1'] as const) {
  const conf = (DUNITER_CONFIG_RAW as Record<string, { providers?: Record<string, string> }>)[network];

  if (conf?.providers) {
    for (const url of Object.values(conf.providers)) {
      providerToNetwork.set(url, network);
    }
  }
}

// Function to determine the network from the API URL
const getNetworkFromApiUrl = (apiUrl: string): 'g1' | 'gtest' | 'gdev' => {
  // Exact match against known provider URLs
  const exact = providerToNetwork.get(apiUrl);

  if (exact) {
    return exact;
  }

  // Fallback: substring matching for URLs not in the config
  if (apiUrl.includes('gdev')) {
    return 'gdev';
  }

  if (apiUrl.includes('gtest') || apiUrl.includes('/gt.') || apiUrl.includes('//gt.')) {
    return 'gtest';
  }

  if (apiUrl.includes('g1') && !apiUrl.includes('gtest') && !apiUrl.includes('gdev')) {
    return 'g1';
  }

  return getDefaultNetwork();
};

// Create a silent initial client (no-op link) to avoid Apollo errors
// before async endpoint resolution completes
const createInitialClient = () => {
  currentNetwork = getNetworkFromApiUrl(settings.get().apiUrl);

  const noopLink = new ApolloLink(() => Observable.of());

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: noopLink
  });
};

// Function to retrieve endpoints
const fetchEndpoints = async (): Promise<string[]> => {
  const config = await loadNetworkConfig(currentNetwork);

  // A network with no Dunipod deployed yet (gdev today) yields an empty list.
  // That is a supported state: the client stays inert and names fall back to
  // raw addresses, rather than the app failing to start.
  return config.dunipod ?? [];
};

// Function to check the height of a Dunipod HTTP endpoint
const checkDunipodHeight = async (endpoint: string): Promise<number> => {
  try {
    // Convert WebSocket URL to HTTP URL
    const httpEndpoint = endpoint.replace('wss://', 'https://').replace('ws://', 'http://');

    // Check height via GraphQL query

    const query = `
      query {
        blocks(first: 1, orderBy: [HEIGHT_DESC]) {
          nodes {
            height
          }
        }
      }
    `;

    // Add a timeout to avoid requests that take too long
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 5 second timeout

    // Try with standard mode first
    try {
      const response = await fetch(httpEndpoint, {
        body: JSON.stringify({ query }),
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        method: 'POST',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json() as { data?: { blocks?: { nodes?: { height?: number }[] } } };
        const height = result.data?.blocks?.nodes?.[0]?.height;

        if (height === undefined) {
          console.warn(`Invalid response from ${endpoint}`);

          return -1;
        }

        // height retrieved successfully

        return height;
      }

      console.warn(`Failed to check height for ${endpoint}: ${response.status} ${response.statusText}`);
    } catch (fetchError: unknown) {
      clearTimeout(timeoutId);

      // If it's a CORS error, we note it specifically
      if (fetchError instanceof TypeError && fetchError.message.includes('CORS')) {
        console.warn(`CORS error for ${endpoint}, endpoint might be valid but not accessible from browser`);

        // Return 0 to indicate that the endpoint might be valid but is not accessible
        // This will allow it to be used as a fallback if no other endpoint is available
        return 0;
      }

      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        // timeout
      } else {
        console.warn(`Fetch error checking height for ${endpoint}:`, fetchError);
      }
    }

    return -1;
  } catch (error) {
    console.warn(`Error checking height for ${endpoint}:`, error);

    return -1;
  }
};

// Function to test the WebSocket connectivity of an endpoint
const testWebSocketConnection = async (endpoint: string): Promise<boolean> => {
  // Convert HTTP URL to WebSocket URL if necessary
  const wsUrl = endpoint.startsWith('http')
    ? endpoint.replace('http://', 'ws://').replace('https://', 'wss://')
    : endpoint;

  return new Promise<boolean>((resolve) => {
    try {
      // test WS connectivity

      // Create a timeout to avoid blocking for too long
      const timeout = setTimeout(() => {
        // WS timeout
        resolve(false);
      }, 5000);

      // Attempt to create a WebSocket connection
      const ws = new WebSocket(wsUrl);

      // Handle connection events
      ws.onopen = () => {
        // WS connected
        clearTimeout(timeout);
        ws.close();
        resolve(true);
      };

      ws.onerror = (error) => {
        // WS error
        clearTimeout(timeout);
        resolve(false);
      };
    } catch (error) {
      // WS test error
      resolve(false);
    }
  });
};

// Function to find the endpoint with the highest block height
const findBestEndpoint = async (endpoints: string[]): Promise<string> => {
  // Check all endpoint heights in parallel

  if (endpoints.length === 0) {
    console.error(`No Dunipod endpoints available for network: ${currentNetwork}`);

    // Return a dummy value, initialization will fail gracefully
    return '';
  }

  // Check the height of all endpoints in parallel
  const heightChecks = await Promise.allSettled(
    endpoints.map(async (endpoint) => {
      const height = await checkDunipodHeight(endpoint);

      return { endpoint, height };
    })
  );

  // Filter for successful and valid results (height > 0)
  const validResults = heightChecks
    .filter((result): result is PromiseFulfilledResult<{endpoint: string, height: number}> =>
      result.status === 'fulfilled' && result.value.height > 0
    )
    .map((result) => result.value);

  // Filter for potentially valid results with CORS issues (height = 0)
  const corsResults = heightChecks
    .filter((result): result is PromiseFulfilledResult<{endpoint: string, height: number}> =>
      result.status === 'fulfilled' && result.value.height === 0
    )
    .map((result) => result.value);

  // Sort by height descending
  validResults.sort((a, b) => b.height - a.height);

  // CORS endpoints tracked for fallback

  // If no valid endpoints are found, try using an endpoint with CORS
  if (validResults.length === 0) {
    if (corsResults.length > 0) {
      // Test WebSocket connectivity for endpoints with CORS
      for (const { endpoint } of corsResults) {
        const wsConnected = await testWebSocketConnection(endpoint);

        if (wsConnected) {
          // found working WS for CORS endpoint

          return endpoint;
        }
      }

      // fallback to CORS endpoint

      return corsResults[0].endpoint;
    }

    console.warn('[dunipod] No valid endpoints found, using first endpoint');

    return endpoints[0];
  }

  console.log(`[dunipod] Best endpoint: ${validResults[0].endpoint} (height: ${validResults[0].height})`);

  return validResults[0].endpoint;
};

const createNewClient = (url: string) => {
  // Ensure URL uses HTTP/HTTPS for HTTP link
  const httpUrl = url.startsWith('ws')
    ? url.replace('ws://', 'http://').replace('wss://', 'https://')
    : url;

  // Convert HTTP URL to WebSocket URL for subscriptions
  const wsUrl = httpUrl.replace('http://', 'ws://').replace('https://', 'wss://');

  // Create split link client (HTTP for queries, WS for subscriptions)

  // HTTP link for queries and mutations
  const httpLink = new HttpLink({
    uri: httpUrl
  });

  // WebSocket link for subscriptions
  const wsLink = new GraphQLWsLink(createClient({
    connectionParams: {
      timeout: 5000
    },
    retryAttempts: 3,
    url: wsUrl
  }));

  // Split link: use WebSocket for subscriptions, HTTP for queries/mutations
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);

      // Type guard to check if definition is an OperationDefinition with subscription
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      return (definition as any).kind === 'OperationDefinition' && (definition as any).operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  const errorLink = onError((errorResponse) => {
    const { forward, operation } = errorResponse;
    // Use networkError and graphQLErrors for Apollo Client 3.x compatibility
    // These will be migrated to the unified 'error' property in Apollo Client 4.0
    // eslint-disable-next-line deprecation/deprecation
    const networkError = 'networkError' in errorResponse ? errorResponse.networkError : null;
    // eslint-disable-next-line deprecation/deprecation
    const graphQLErrors = 'graphQLErrors' in errorResponse ? errorResponse.graphQLErrors : null;

    // Log GraphQL errors for debugging
    if (graphQLErrors && graphQLErrors.length > 0) {
      graphQLErrors.forEach((err) => {
        const locationStr = err.locations ? JSON.stringify(err.locations) : 'unknown';
        const pathStr = err.path ? JSON.stringify(err.path) : 'unknown';

        console.error(
          `[GraphQL error]: Message: ${err.message}, Location: ${locationStr}, Path: ${pathStr}`,
          err.extensions
        );
      });
    }

    // Handle network errors by switching to next endpoint
    if (networkError) {
      console.error(`[Network error]: ${networkError.message}`);

      return new Observable((observer) => {
        console.error('Switching to next endpoint after network error...');
        switchToNextEndpoint()
          .then(() => {
            const subscriber = {
              complete: observer.complete.bind(observer),
              error: observer.error.bind(observer),
              next: observer.next.bind(observer)
            };

            execute(dunipodClient.link, operation).subscribe(subscriber);
          })
          .catch((error) => {
            observer.error(error);
          });
      });
    }

    return forward(operation);
  });

  return new ApolloClient({
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      },
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      }
    },
    link: ApolloLink.from([
      errorLink,
      splitLink
    ])
  });
};

const switchToNextEndpoint = async () => {
  const nextIndex = currentEndpointIndex + 1;

  currentEndpointIndex = nextIndex >= ENDPOINTS.length ? 0 : nextIndex;

  const nextEndpoint = ENDPOINTS[currentEndpointIndex];

  console.warn(`[dunipod] Switching to endpoint: ${nextEndpoint}`);

  const newClient = createNewClient(nextEndpoint);

  if (currentClient) {
    currentClient.stop();
    await currentClient.clearStore();
  }

  currentClient = newClient;
  dunipodClient = newClient;
  dunipodClientState.emit();
};

// Initialize endpoints and client
const initializeClient = async () => {
  try {
    // Retrieve the list of endpoints
    ENDPOINTS = await fetchEndpoints();
    console.log(`[dunipod] ${ENDPOINTS.length} endpoints for ${currentNetwork}`);

    // Expose endpoints to the global environment
    if (typeof window !== 'undefined') {
      window.__DUNIPOD_ENDPOINTS__ = ENDPOINTS;
    }

    if (ENDPOINTS.length === 0) {
      console.warn(`No dunipod endpoints for network ${currentNetwork}, client will not be functional.`);

      return;
    }

    // Find the best endpoint (the one with the highest height)
    const bestEndpoint = await findBestEndpoint(ENDPOINTS);

    // Find the index of the best endpoint
    currentEndpointIndex = ENDPOINTS.indexOf(bestEndpoint);

    if (currentEndpointIndex === -1) {
      currentEndpointIndex = 0;
      console.warn('Best endpoint not found in list, using first endpoint');
    }

    // Create a client with the best endpoint
    const initialClient = createNewClient(ENDPOINTS[currentEndpointIndex]);

    if (currentClient) {
      currentClient.stop();
      await currentClient.clearStore();
    }

    currentClient = initialClient;
    dunipodClient = initialClient;
    dunipodClientState.emit();
  } catch (error) {
    console.error('Failed to initialize Dunipod client:', error);
  }
};

// Export an instance of the client initialized with the first endpoint
// This variable will be updated when the endpoint changes
export let dunipodClient = createInitialClient();

currentClient = dunipodClient;

// Initialize the client with the endpoints retrieved from the JSON file
// This operation is asynchronous and will update dunipodClient once completed
initializeClient().catch(console.error);

// Listen for network changes and re-initialize the client
settings.on('change', (newSettings) => {
  const newNetwork = getNetworkFromApiUrl(newSettings.apiUrl);

  if (newNetwork !== currentNetwork) {
    console.log(`[dunipod] Network changed: ${currentNetwork} -> ${newNetwork}`);
    currentNetwork = newNetwork;
    initializeClient().catch(console.error);
  }
});
